import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useHistory, useParams } from "react-router";
import Swal from "sweetalert2";
import * as Yup from "yup";
import Select from "react-select";
import { URL_REGEX } from "../../../utils/Constants";
import {
  GetBrandSettingCommonContent,
  AddBrandSettingAPI,
  EditBrandSettingAPI,
  GetNetsuiteERPBrandNameList,
} from "../../../services/BrandServices";
import { GetCampusMgtList } from "../../../services/CampusMgtService";
import { GetStatemantTmpList } from "../../../services/GradeSettingService";
import ImageUploadField from "../../courses/CourseGradeSettings/ImageUploadField";
import useStickyFooterScrollPadding from "../../../hooks/useStickyFooterScrollPadding";

const VIDEO_FORMATS = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];

const BrandSettingForm = ({ isEdit = false, brandSettingData = {}, onFinalSubmit }) => {
  const history = useHistory();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [CRMBrandName, setCRMBrandName] = useState([]);
  const [ERPBrandName, setERPBrandName] = useState([]);
  const [brandWelcomeLetter, setBrandWelcomeLetter] = useState([]);
  const [campusList, setCampusList] = useState([]);
  const [sorTemplateList, setSorTemplateList] = useState([]);
  const stickyFooterRef = useRef(null);
  useStickyFooterScrollPadding(stickyFooterRef);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [commonContent, erpBrands, campusData, sorTemplatesRes] = await Promise.all([
          GetBrandSettingCommonContent().catch(() => ({
            data: {
              brand_reg_crm: [],
              erp_brand_name: [],
              brand_welcome_letter: [],
            },
          })),
          GetNetsuiteERPBrandNameList().catch(() => ({
            data: {
              data: {
                items: [],
              },
            },
          })),
          GetCampusMgtList({}).catch(() => ({
            data: {
              data_list: [],
            },
          })),
          GetStatemantTmpList({
            page: 1,
            limit: 10,
            search: "",
            key: "name",
            sort: "asc",
            exportStatus: "true",
          }).catch(() => ({
            data: {
              result: [],
            },
          })),
        ]);

        setCRMBrandName(
          commonContent?.data?.brand_reg_crm?.map((data) => ({
            value: `${data.value}`,
            label: `${data.title}`,
          })) || []
        );

        setBrandWelcomeLetter(
          commonContent?.data?.brand_welcome_letter?.map((data) => ({
            value: `${data.value}`,
            label: `${data.title}`,
          })) || []
        );

        const sorTemplates = Array.isArray(sorTemplatesRes?.data?.result)
          ? sorTemplatesRes.data.result.map((template) => ({
              value: template.id,
              label: template.name,
            }))
          : [];

        setSorTemplateList(sorTemplates);

        setERPBrandName(
          erpBrands?.data?.data?.items?.map((data) => ({
            value: `${data.id}`,
            label: `${data.fullname}`,
          })) || []
        );

        setCampusList(
          campusData?.data?.data_list?.map((val) => ({
            label: val.campus_name,
            value: val.id,
          })) || []
        );
      } catch (error) {
        console.error("BrandSettingForm.jsx->fetchData->Error", error);
      }
    };

    fetchData();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Section 1: Details
      PortalTitle: isEdit ? (brandSettingData?.title || "") : "",
      BrandName: isEdit ? (brandSettingData?.brand_name || "") : "",
      Domain: isEdit ? (brandSettingData?.domain_url || "") : "",
      ERPBrandName: isEdit ? (brandSettingData?.erp_brand || "") : "",
      ERPBrandNameID: isEdit ? (brandSettingData?.erp_brand_id || "") : "",
      CRMBrandName: isEdit ? (brandSettingData?.brand_reg_crm || "") : "",
      campus_locations: isEdit
        ? (brandSettingData?.campus_location
          ? (Array.isArray(brandSettingData.campus_location)
            ? brandSettingData.campus_location
            : brandSettingData.campus_location.split(","))
          : []).map(Number)
        : [],
      Mobile: isEdit ? (brandSettingData?.contact || "") : "",
      EmailDomain: isEdit ? (brandSettingData?.microsoft_email || "") : "",
      tandc_url: isEdit ? (brandSettingData?.tandc_url || "") : "",

      // Section 2: Apps and Templates
      hauwei: isEdit ? (brandSettingData?.hauweiAppUrl || "") : "",
      IOS_url: isEdit ? (brandSettingData?.iosAppUrl || "") : "",
      Android: isEdit ? (brandSettingData?.androidAppUrl || "") : "",
      StudentCardQR: isEdit ? (brandSettingData?.studentCardVerifyUrl || "") : "",
      BrandWelcomeLetter: isEdit ? (brandSettingData?.brand_welcome_letter || "") : "",
      sor_template: isEdit ? (brandSettingData?.sor_template || brandSettingData?.st_template || "") : "",

      // Section 3: Visuals
      pmc_rereg_video_iframe: isEdit ? (brandSettingData?.pmc_rereg_video_iframe || "") : "",
      pmc_rereg_fallback_video: isEdit ? (brandSettingData?.pmc_rereg_fallback_video || "") : "",
      LetterheadHeader: isEdit ? (brandSettingData?.letterheadHeader || "") : "",
      LetterheadFooter: isEdit ? (brandSettingData?.letterheadFooter || "") : "",
      photo: isEdit ? (brandSettingData?.studentCardBackground || "") : "",
      signature: isEdit ? (brandSettingData?.brandSignature || "") : "",
      stamp: isEdit ? (brandSettingData?.stamp_attachment || brandSettingData?.stamp_attachment || "") : "",
    },
    validationSchema: Yup.object({
      // Section 1 Validation
      PortalTitle: Yup.string().required("Portal Title is required").trim("Enter valid title"),
      BrandName: Yup.string()
        .trim("The Brand name cannot include leading and trailing spaces")
        .required("Brand Name is required"),
      Domain: Yup.string()
        .url("Please Enter Domain URL")
        .matches(URL_REGEX, "Domain URL is not valid")
        .required("Domain Url Is Required"),
      CRMBrandName: Yup.string().required("CRM Brand Name is required"),
      campus_locations: Yup.array().min(1, "Campus Location is required"),
      Mobile: Yup.string().required("Contact Number Is Required"),
      EmailDomain: Yup.string()
        .required("Email Domain Is Required")
        .trim("Enter Valid Email Domain"),
      tandc_url: Yup.string()
        .url("Please Enter Terms & Condition URL")
        .matches(URL_REGEX, "Terms & Conditions URL is not valid")
        .required("Terms & Condition URL is required"),

      // Section 2 Validation
      hauwei: Yup.string().url("Please Enter Hauwei URL").matches(URL_REGEX, "Hauwei URL is not valid"),
      IOS_url: Yup.string().url("Please Enter IOS URL").matches(URL_REGEX, "IOS URL is not valid"),
      Android: Yup.string().url("Please Enter Android URL").matches(URL_REGEX, "Android URL is not valid"),
      StudentCardQR: Yup.string()
        .url("Please Enter Student Card QR URL")
        .matches(URL_REGEX, "Student Card QR URL is not valid")
        .required("Student Card QR Url Is Required"),
      BrandWelcomeLetter: Yup.mixed().required("Welcome Letter is required"),
      sor_template: Yup.string().required("SOR Template is required"),

      // Section 3 Validation
      pmc_rereg_video_iframe: Yup.string(),
      pmc_rereg_fallback_video: Yup.mixed(),
      LetterheadHeader: Yup.mixed().required(
        isEdit ? "Letterhead Header image is required" : "Letterhead Header image Required"
      ),
      LetterheadFooter: Yup.mixed().required(
        isEdit ? "Letterhead Footer image is required" : "Letterhead Footer image Required"
      ),
      photo: isEdit ? Yup.mixed() : Yup.mixed().required("Student Card image Required"),
      signature: Yup.mixed().required("Signature is required"),
      stamp: Yup.mixed(),
    }),
    onSubmit: (values) => {
      setDisabled(true);
      let formData = new FormData();

      if (isEdit) {
        formData.set("type", "brand_setting");
        formData.set("id", brandSettingData?.id || id);
        formData.set("title", values?.PortalTitle);
        formData.set("brand_name", values?.BrandName);
        formData.set("domain_url", values?.Domain);
        formData.set("erp_brand", values?.ERPBrandName || "");
        formData.append("erp_brand_id", values?.ERPBrandNameID);
        formData.set("brand_reg_crm", values?.CRMBrandName);
        formData.set("campus_location", values.campus_locations);
        formData.set("contact", values?.Mobile);
        formData.set("microsoft_email", values?.EmailDomain);
        formData.set("tandc_url", values?.tandc_url);

        formData.set("hauweiAppUrl", values?.hauwei);
        formData.set("iosAppUrl", values?.IOS_url);
        formData.set("androidAppUrl", values?.Android);
        formData.set("studentCardVerifyUrl", values?.StudentCardQR);
        formData.set("brand_welcome_letter", values?.BrandWelcomeLetter);
        formData.set("sor_template", values?.sor_template);

        formData.set("pmc_rereg_video_iframe", values.pmc_rereg_video_iframe);
        formData.set("pmc_rereg_fallback_video", values.pmc_rereg_fallback_video);
        formData.append("letterheadHeader", values.LetterheadHeader);
        formData.append("letterheadFooter", values.LetterheadFooter);
        formData.append("studentCardBackground", values.photo);
        formData.append("brandSignature", values.signature);
        if (values.stamp) formData.append("stamp_attachment", values.stamp);

        EditBrandSettingAPI(formData)
          .then((res) => {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Updated successfully",
            });
            history.push(`/administration/brandSetting/editPortalSetting/visualsTab/${id || brandSettingData?.id}`);
            setDisabled(false);
          })
          .catch((err) => {
            setDisabled(false);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: err?.response?.data?.message || "Something went wrong",
            });
          });
      } else {
        formData.append("title", values?.PortalTitle);
        formData.append("brand_name", values?.BrandName);
        formData.append("domain_url", values?.Domain);
        formData.append("erp_brand", values?.ERPBrandName);
        formData.append("erp_brand_id", values?.ERPBrandNameID);
        formData.append("brand_reg_crm", values?.CRMBrandName);
        formData.append("campus_location", values.campus_locations);
        formData.append("contact", values?.Mobile);
        formData.append("microsoft_email", values?.EmailDomain);
        formData.append("tandc_url", values?.tandc_url);

        formData.set("hauweiAppUrl", values?.hauwei);
        formData.append("iosAppUrl", values?.IOS_url);
        formData.append("androidAppUrl", values?.Android);
        formData.append("studentCardVerifyUrl", values?.StudentCardQR);
        formData.append("brand_welcome_letter", values.BrandWelcomeLetter);
        formData.set("sor_template", values?.sor_template);

        formData.set("pmc_rereg_video_iframe", values.pmc_rereg_video_iframe);
        formData.set("pmc_rereg_fallback_video", values.pmc_rereg_fallback_video);
        formData.append("letterheadHeader", values.LetterheadHeader);
        formData.append("letterheadFooter", values.LetterheadFooter);
        formData.append("studentCardBackground", values.photo);
        formData.append("brandSignature", values.signature);
        if (values.stamp) formData.append("stamp_attachment", values.stamp);

        AddBrandSettingAPI(formData)
          .then((res) => {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Added successfully",
            });
            setDisabled(false);
            if (onFinalSubmit) {
              onFinalSubmit();
            }
            history.push(
              `/administration/brandSetting/editPortalSetting/visualsTab/${res?.data?.inserted_record}`
            );
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: `${err?.response?.data || "Something went wrong"}`,
            });
            setDisabled(false);
            history.push("/administration/brandSetting/addNewBrand");
          });
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="design-token-page">
        {/* SECTION 1: Details */}
        <div className="card card-body-inr mb-4 design-token-page">
          <div className="brand-card-header mb-4">Details</div>
          <div className="row">
            {/* Row 1: Portal Title, Brand Name, Domain URL */}
            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="portalTitle">
                Portal Title <span className="required-star">*</span>
              </label>
              <div className="form-icon-group" title="Title">
                <input
                  type="text"
                  id="portalTitle"
                  className={
                    "form-control" +
                    (formik.errors.PortalTitle && formik.touched.PortalTitle ? " is-invalid" : "")
                  }
                  name="PortalTitle"
                  placeholder="Portal Title"
                  title="Portal Title"
                  value={formik.values.PortalTitle}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="BrandName">
                Brand Name <span className="required-star">*</span>
              </label>
              <div className="form-icon-group" title="Brand Name">
                <input
                  type="text"
                  className={
                    "form-control" +
                    (formik.errors.BrandName && formik.touched.BrandName ? " is-invalid" : "")
                  }
                  name="BrandName"
                  id="BrandName"
                  placeholder="Brand Name"
                  title="Brand Name"
                  value={formik.values.BrandName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="DomainURL">
                Domain URL <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <input
                  type="text"
                  className={
                    "form-control" +
                    (formik.errors.Domain && formik.touched.Domain ? " is-invalid" : "")
                  }
                  name="Domain"
                  id="DomainURL"
                  placeholder="Domain URL"
                  value={formik.values.Domain}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  title="Domain URL"
                />
              </div>
            </div>

            {/* Row 2: Netsuite Brand Name, CRM Brand Name, Campus Locations */}
            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="ERPBrandName">Netsuite Brand Name</label>
              <div className="form-icon-group">
                <Select
                  className={
                    "form-control custom-select-box " +
                    (formik.errors.ERPBrandName && formik.touched.ERPBrandName ? " is-invalid" : "")
                  }
                  name="ERPBrandName"
                  value={ERPBrandName.find(
                    (option) => option.value == formik.values.ERPBrandNameID
                  )}
                  onChange={(value) => {
                    formik.setFieldValue("ERPBrandName", value?.label || "");
                    formik.setFieldValue("ERPBrandNameID", value?.value || "");
                  }}
                  onBlur={formik.handleBlur}
                  options={ERPBrandName}
                  maxMenuHeight={175}
                  placeholder="Select"
                  isClearable
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="CRMBrandName">
                CRM Brand Name <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <Select
                  className={
                    "form-control custom-select-box " +
                    (formik.errors.CRMBrandName && formik.touched.CRMBrandName ? " is-invalid" : "")
                  }
                  name="CRMBrandName"
                  value={CRMBrandName.find(
                    (option) => option.value == formik.values?.CRMBrandName
                  )}
                  onChange={(value) => {
                    formik.setFieldValue("CRMBrandName", value?.value || "");
                  }}
                  onBlur={formik.handleBlur}
                  options={CRMBrandName}
                  maxMenuHeight={175}
                  placeholder="Select"
                  isClearable
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="campus_locations">
                Campus Locations <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <Select
                  className={
                    "form-control custom-select-box" +
                    (formik.errors.campus_locations && formik.touched.campus_locations
                      ? " is-invalid"
                      : "")
                  }
                  name="campus_locations"
                  value={campusList.filter((option) =>
                    formik.values.campus_locations?.includes(option.value)
                  )}
                  onChange={(value) => {
                    formik.setFieldValue(
                      "campus_locations",
                      value?.map((item) => item.value) || []
                    );
                  }}
                  onBlur={formik.handleBlur}
                  options={campusList}
                  maxMenuHeight={175}
                  placeholder="Select"
                  isClearable
                  isMulti
                />
              </div>
            </div>

            {/* Row 3: Contact Number, Email Domain, Terms & Condition URL */}
            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="Contact">
                Contact Number <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <input
                  type="text"
                  className={
                    "form-control" +
                    (formik.errors.Mobile && formik.touched.Mobile ? " is-invalid" : "")
                  }
                  name="Mobile"
                  title="Contact number"
                  id="Contact"
                  placeholder="Enter Contact Number"
                  value={formik.values.Mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="EmailDomain">
                Email Domain <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <input
                  type="text"
                  className={
                    "form-control" +
                    (formik.errors.EmailDomain && formik.touched.EmailDomain ? " is-invalid" : "")
                  }
                  name="EmailDomain"
                  id="EmailDomain"
                  placeholder="Email Domain"
                  value={formik.values.EmailDomain}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  title="Email Domain"
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="tandc_url">
                Terms & Condition URL <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <input
                  type="text"
                  className={
                    "form-control" +
                    (formik.errors.tandc_url && formik.touched.tandc_url ? " is-invalid" : "")
                  }
                  name="tandc_url"
                  id="tandc_url"
                  placeholder="Terms & Condition URL"
                  value={formik.values.tandc_url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  title="Terms & Condition URL"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Apps and Templates */}
        <div className="card card-body-inr mb-4">
          <div className="brand-card-header mb-4">Apps and Templates</div>
          <div className="row">
            {/* Row 1: Hauwei App URL, iOS App URL, Android App URL */}
            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="hauwei">Hauwei App URL</label>
              <div className="form-icon-group">
                <input
                  type="text"
                  id="hauwei"
                  className={
                    "form-control" +
                    (formik.errors.hauwei && formik.touched.hauwei ? " is-invalid" : "")
                  }
                  name="hauwei"
                  title="Hauwei App URL"
                  placeholder="Enter Hauwei URL"
                  value={formik.values.hauwei?.replace(" ", "")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="IOS_url">IOS App URL</label>
              <div className="form-icon-group">
                <input
                  type="text"
                  id="IOS_url"
                  className={
                    "form-control" +
                    (formik.errors.IOS_url && formik.touched.IOS_url ? " is-invalid" : "")
                  }
                  name="IOS_url"
                  title="IOS App URL"
                  placeholder="Enter IOS URL"
                  value={formik.values.IOS_url?.replace(" ", "")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="android">Android App URL</label>
              <div className="form-icon-group">
                <input
                  type="text"
                  id="android"
                  className={
                    "form-control" +
                    (formik.errors.Android && formik.touched.Android ? " is-invalid" : "")
                  }
                  name="Android"
                  title="Android App URL"
                  placeholder="Enter Android URL"
                  value={formik.values.Android?.replace(" ", "")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            {/* Row 2: Student Card QR, Welcome Letter, SOR Template */}
            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="studentCard">
                Student Card QR <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <input
                  type="text"
                  id="studentCard"
                  className={
                    "form-control" +
                    (formik.errors.StudentCardQR && formik.touched.StudentCardQR ? " is-invalid" : "")
                  }
                  name="StudentCardQR"
                  title="Student Card QR"
                  placeholder="Enter Student Card QR URL"
                  value={formik.values.StudentCardQR?.replace(" ", "")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="BrandWelcomeLetter">
                Welcome Letter <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <Select
                  className={
                    "form-control custom-select-box" +
                    (formik.errors.BrandWelcomeLetter && formik.touched.BrandWelcomeLetter
                      ? " is-invalid"
                      : "")
                  }
                  name="BrandWelcomeLetter"
                  value={brandWelcomeLetter.find(
                    (option) => option.value == formik.values.BrandWelcomeLetter
                  )}
                  onChange={(value) => {
                    formik.setFieldValue("BrandWelcomeLetter", value?.value || "");
                  }}
                  onBlur={formik.handleBlur}
                  options={brandWelcomeLetter}
                  maxMenuHeight={175}
                  placeholder="Select"
                  isClearable
                />
              </div>
            </div>

            <div className="col-md-6 col-lg-4 mb-4">
              <label htmlFor="sor_template">
                SOR Template <span className="required-star">*</span>
              </label>
              <div className="form-icon-group">
                <Select
                  className={
                    "form-control custom-select-box" +
                    (formik.errors.sor_template && formik.touched.sor_template ? " is-invalid" : "")
                  }
                  name="sor_template"
                  value={sorTemplateList.find(
                    (option) => option.value == formik.values.sor_template
                  )}
                  onChange={(value) => {
                    formik.setFieldValue("sor_template", value?.value || "");
                  }}
                  onBlur={formik.handleBlur}
                  options={sorTemplateList}
                  maxMenuHeight={175}
                  placeholder="Select"
                  isClearable
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 3: Visuals */}
        <div className="card card-body-inr">
          <div className="brand-card-header mb-4">Visuals</div>
          <div className="row">
            {/* Row 1: PMC Re-Reg Video iFrame (Full width) */}
            <div className="col-md-12 mb-4">
              <label htmlFor="pmc_rereg_video_iframe">
                PMC Re-Reg Video iFrame
              </label>
              <div className="form-icon-group" title="Title">
                <input
                  type="text"
                  id="pmc_rereg_video_iframe"
                  className={
                    "form-control" +
                    (formik.errors.pmc_rereg_video_iframe && formik.touched.pmc_rereg_video_iframe
                      ? " is-invalid"
                      : "")
                  }
                  name="pmc_rereg_video_iframe"
                  placeholder="PMC Re-Reg Video Iframe"
                  title="PMC Re-Reg Video Iframe"
                  value={formik.values.pmc_rereg_video_iframe || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            {/* Row 2: PMC Re-Reg Fallback Video, Letterhead Header, Letterhead Footer */}
            <ImageUploadField
              label="PMC Re-Reg Fallback Video"
              fieldName="pmc_rereg_fallback_video"
              formik={formik}
              accept={VIDEO_FORMATS}
              isVideo
              showRemove={false}
              previewType="brand"
              className="col-md-6 col-lg-4 mb-4"
            />
            <ImageUploadField
              label="Letterhead Header"
              fieldName="LetterheadHeader"
              formik={formik}
              required
              showRemove={false}
              previewType="brand"
              className="col-md-6 col-lg-4 mb-4"
            />
            <ImageUploadField
              label="Letterhead Footer"
              fieldName="LetterheadFooter"
              formik={formik}
              required
              showRemove={false}
              previewType="brand"
              className="col-md-6 col-lg-4 mb-4"
            />

            {/* Row 3: Student Card Background, Signature, Stamp */}
            <ImageUploadField
              label="Student Card Background"
              fieldName="photo"
              formik={formik}
              required={!isEdit}
              showRemove={false}
              previewType="brand"
              className="col-md-6 col-lg-4 mb-4"
            />
            <ImageUploadField
              label="Signature"
              fieldName="signature"
              formik={formik}
              required
              showRemove={false}
              previewType="brand"
              className="col-md-6 col-lg-4 mb-4"
            />
            <ImageUploadField
              label="Stamp"
              fieldName="stamp"
              formik={formik}
              showRemove={false}
              previewType="brand"
              className="col-md-6 col-lg-4 mb-4"
            />
          </div>
        </div>

        {/* Form Action Buttons */}
        <div ref={stickyFooterRef} className="form-group form-group-save-cancel sticky-save-footer mb-0">
          <button
            className="btn btn-save btn-success"
            type="submit"
            title="Save"
            disabled={disabled}
          >
            {disabled ? (
              <i className="fas fa-cog fa-spin"></i>
            ) : (
              <i className="fal fa-save"></i>
            )}
            Save
          </button>
          <button
            className="btn btn-close btn-danger"
            type="button"
            title="Cancel"
            onClick={() => history.goBack()}
          >
            <i className="fal fa-times"></i>
            Cancel
          </button>

          {Object.keys(formik.values).map((key) => {
            if (formik.touched[key] && formik.errors[key]) {
              return (
                <div className="invalid-feedback d-block" key={key}>
                  {formik.errors[key]}
                </div>
              );
            }
            return null;
          })}
        </div>
      </form>
  );
};

export default BrandSettingForm;
